import { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import type { FormikHelpers, FormikProps } from 'formik';
import type { ReactNode } from 'react';
import type { AnyObject, ObjectSchema } from 'yup';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { useDialogAutoFocus } from '../../hooks/useDialogAutoFocus.hook';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';

import { MemoBeforeSubmitForm } from '../MemoBeforeSubmit';
import { useMemos } from '../MemoBeforeSubmit/hooks/useMemos.hook';
import { AlertMessages } from '../../components/AlertMessages';

import type { SubmitHandler } from './types/submit-handler.type';

interface Props<V extends object> {
  readonly askMemoBeforeSubmit?: boolean;
  readonly autoFocusSubmit?: boolean;
  readonly children?: (formik: FormikProps<V>) => ReactNode;
  readonly description: string;
  readonly id: string;
  readonly initialValues: V;
  readonly isDialogOpen: boolean;
  readonly onCloseDialog: () => void;
  readonly onSubmit: SubmitHandler<V>;
  readonly submitLabel: string;
  readonly title: string;
  readonly validationSchema?: ObjectSchema<AnyObject>;
}

export const MemoConfirmDialog = <V extends object>({
  askMemoBeforeSubmit = true,
  autoFocusSubmit,
  children,
  description,
  id,
  initialValues,
  isDialogOpen,
  onCloseDialog: handleCloseDialog,
  onSubmit: pHandleSubmit,
  submitLabel,
  title,
  validationSchema,
}: Readonly<Props<V>>) => {
  const { ui } = useUiContext();

  const shouldAutoFocusSubmit = autoFocusSubmit ?? !children;

  const dialogAutoFocusSlotProps = useDialogAutoFocus();

  const [formError, setFormError] = useState<readonly string[] | null>(null);

  const { clearMemos, memos, setMemos } = useMemos();

  const handleSubmit = useCallback(
    (values: V, formikHelpers: FormikHelpers<V>) => {
      void pHandleSubmit({ values, memos, formikHelpers, setFormError })?.then(
        clearMemos,
      );
    },
    [clearMemos, pHandleSubmit, memos],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {(formik: FormikProps<V>) => {
        const { isSubmitting, isValid, submitForm } = formik;
        const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

        return (
          <Form onSubmit={formik.handleSubmit}>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
            {!!askMemoBeforeSubmit && (
              <MemoBeforeSubmitForm
                clearMemos={clearMemos}
                isLoading={isLoading}
                memos={memos}
                onConfirm={submitForm}
                setMemos={setMemos}
                submitLabel={submitLabel}
              />
            )}
            <Dialog
              aria-describedby={`${id}-dialog-description`}
              aria-labelledby={`${id}-dialog-title`}
              onClose={handleCloseDialog}
              open={isDialogOpen}
              slotProps={dialogAutoFocusSlotProps}
            >
              <DialogTitle id={`${id}-dialog-title`}>{title}</DialogTitle>
              <DialogContent>
                <DialogContentText id={`${id}-dialog-description`}>
                  {description}
                </DialogContentText>
                {children?.(formik)}
                {!!formError && (
                  <AlertMessages messages={formError} severity="error" />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} size="large">
                  Annuler
                </Button>
                <Button
                  color="warning"
                  data-dialog-autofocus={shouldAutoFocusSubmit}
                  disabled={!isValid}
                  loading={isLoading}
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={
                    askMemoBeforeSubmit
                      ? () => {
                          setMemos((oldMemos) => ({
                            ...oldMemos,
                            isOpenMemoDialog: true,
                          }));
                        }
                      : submitForm
                  }
                  size="large"
                  variant="contained"
                >
                  {submitLabel}
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};
